<script lang="ts">
  import { GROUP_LABELS, UPGRADES, type UpgradeDef, type UpgradeGroup } from '../../core/balance';
  import { purchaseStatus } from '../../core/engine';
  import Icon from '../../shared/components/Icon.svelte';
  import { sound } from '../../shared/lib/audio';
  import { fmt } from '../../shared/lib/format';
  import { game } from '../../shared/lib/game.svelte';
  import { settings } from '../../shared/lib/settings.svelte';
  import { toasts } from '../../shared/lib/toasts.svelte';

  const groups = (Object.keys(GROUP_LABELS) as UpgradeGroup[]).map((g) => ({
    id: g,
    label: GROUP_LABELS[g],
    items: UPGRADES.filter((u) => u.group === g),
  }));

  const cards: Record<string, HTMLElement> = {};

  function purchase(u: UpgradeDef) {
    sound.unlock();
    const level = game.buy(u.id);
    if (level === null) return;
    sound.purchase();
    toasts.push({
      tone: 'accent',
      badge: `${level}`,
      title: `${u.name} ${level}`,
      body: u.effect(level),
    });
    const card = cards[u.id];
    if (card && !settings.reducedMotion) {
      card.classList.remove('bought');
      void card.offsetWidth; // restart the animation on repeat purchases
      card.classList.add('bought');
    }
  }
</script>

<div class="view">
  <header class="view-head">
    <h1>Upgrades</h1>
    <p>Spend points to see further, earn more from primes, and cover ground faster.</p>
  </header>

  {#each groups as g (g.id)}
    <section class="group" aria-labelledby="g-{g.id}">
      <h2 id="g-{g.id}" class="eyebrow">{g.label}</h2>
      <div class="grid">
        {#each g.items as u (u.id)}
          {@const level = game.s.upgrades[u.id]}
          {@const st = purchaseStatus(game.s, u.id)}
          <article
            class="card"
            data-state={st.kind === 'available' ? (st.affordable ? 'ready' : 'saving') : st.kind}
            bind:this={cards[u.id]}
          >
            <div class="head">
              <h3>{u.name}</h3>
              <span class="pips" aria-label="Level {level} of {u.costs.length}">
                {#each u.costs as _, i (i)}
                  <span class="pip" class:on={i < level}></span>
                {/each}
              </span>
            </div>
            <p class="blurb">{u.blurb}</p>

            <dl class="effect">
              {#if level > 0}
                <div>
                  <dt>Now</dt>
                  <dd>{u.effect(level)}</dd>
                </div>
              {/if}
              {#if st.kind !== 'maxed'}
                <div class="next">
                  <dt>{level > 0 ? 'Next' : 'Gives'}</dt>
                  <dd>{u.effect(level + 1)}</dd>
                </div>
              {/if}
            </dl>

            <div class="foot">
              {#if st.kind === 'maxed'}
                <span class="state"><Icon name="check" size={16} /> Fully upgraded</span>
              {:else if st.kind === 'locked'}
                <span class="state"><Icon name="lock" size={16} /> Needs {st.needs}</span>
              {:else}
                <button
                  class="btn buy"
                  class:btn-primary={st.affordable}
                  disabled={!st.affordable}
                  onclick={() => purchase(u)}
                >
                  {st.affordable ? 'Buy' : 'Need'} <span class="num">{fmt(st.cost)}</span>
                </button>
                {#if !st.affordable}
                  <span class="save num" aria-hidden="true">
                    <span class="bar"
                      ><span style:transform="scaleX({Math.min(1, game.s.points / st.cost)})"
                      ></span></span
                    >
                    {fmt(st.cost - game.s.points)} to go
                  </span>
                {/if}
              {/if}
            </div>
          </article>
        {/each}
      </div>
    </section>
  {/each}
</div>

<style>
  .group {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
    gap: var(--space-3);
  }

  .card {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-4);
    border-radius: var(--radius);
    background: var(--bg-raised);
    border: 1px solid var(--line);
    min-width: 0;
    transition: border-color var(--t) var(--ease-out);
  }
  .card[data-state='ready'] {
    border-color: color-mix(in srgb, var(--accent) 55%, var(--line));
  }
  .card:global(.bought) {
    animation: bought 640ms var(--ease-out);
  }
  @keyframes bought {
    0% {
      box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 50%, transparent);
      transform: scale(1.015);
    }
    100% {
      box-shadow: 0 0 0 14px transparent;
      transform: scale(1);
    }
  }
  .card[data-state='locked'] {
    background: transparent;
    border-style: dashed;
  }
  .card[data-state='locked'] :is(h3, .blurb, .effect) {
    opacity: 0.6;
  }

  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-3);
  }
  h3 {
    font-size: var(--text-md);
  }

  .pips {
    display: inline-flex;
    gap: 4px;
  }
  .pip {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    border: 1.5px solid var(--ink-3);
    transition:
      background-color var(--t-slow) var(--ease-spring),
      border-color var(--t-slow) var(--ease-out),
      transform var(--t-slow) var(--ease-spring);
  }
  .pip.on {
    background: var(--accent);
    border-color: var(--accent);
    transform: scale(1.1);
  }

  .blurb {
    font-size: var(--text-sm);
    color: var(--ink-2);
  }

  .effect {
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-size: var(--text-sm);
  }
  .effect div {
    display: flex;
    gap: var(--space-2);
  }
  .effect dt {
    width: 3.5em;
    flex: none;
    color: var(--ink-3);
  }
  .effect dd {
    margin: 0;
    font-weight: 600;
  }
  .effect .next dd {
    color: var(--accent);
  }

  .foot {
    margin-top: auto;
    padding-top: var(--space-2);
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .state {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--text-sm);
    color: var(--ink-3);
    font-weight: 600;
  }

  .save {
    display: inline-flex;
    flex-direction: column;
    gap: 4px;
    font-size: var(--text-xs);
    color: var(--ink-3);
    flex: 1;
    min-width: 90px;
  }
  .bar {
    height: 4px;
    border-radius: 2px;
    background: var(--bg-sunken);
    overflow: hidden;
  }
  .bar span {
    display: block;
    height: 100%;
    background: color-mix(in srgb, var(--accent) 70%, var(--ink-3));
    transform-origin: left;
    transition: transform var(--t) var(--ease-out);
  }
</style>
